import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ShieldCheck, ArrowLeft, Users, Buildings, Folder, Trash, Key, PencilSimple,
  MagnifyingGlass, Copy, CheckCircle, Article, Sparkle,
  EnvelopeSimple, Briefcase, Eye, X, ClockCounterClockwise, Check, XCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  saListUsers, saDeleteUser, saBulkDeleteUsers, saResetUserPassword, saUpdateUser,
  saListCompanies, saRenameCompany, saDeleteCompany, saBulkDeleteCompanies, saResetCompanyPassword,
  saListProjects, saDeleteProject, saBulkDeleteProjects,
  listContactSubmissions, listJobApplications,
  updateContactStatus, updateApplicationStatus,
  deleteContactSubmission, deleteJobApplication,
  adminPending, adminApprove, adminReject,
  getEnterpriseDashboard,
} from "@/lib/api";
import YourDomainBanner from "@/components/YourDomainBanner";
import RolesTab from "@/pages/superadmin/tabs/RolesTab";
import PermissionsTab from "@/pages/superadmin/tabs/PermissionsTab";
import InvitationsTab from "@/pages/superadmin/tabs/InvitationsTab";
import SignatureRegistryTab from "@/pages/superadmin/tabs/SignatureRegistryTab";

/**
 * Super Admin Management Console — hard-gated to `role === "superadmin"` via
 * ProtectedRoute in App.js. Provides Users / Companies / Projects tabs with
 * bulk actions and temporary-password generation.
 */
export default function SuperAdminConsole({ initialTab }) {
  // Accept a controlled starting tab via prop OR URL param so
  // /app/superadmin/users /companies /projects /pending /audit /roles /permissions /invitations
  // are directly deep-linkable (hotfix · Feb 2026).
  const validTabs = ["dashboard", "pending", "users", "companies", "projects",
                     "roles", "permissions", "invitations", "audit", "signatures"];
  const startTab  = validTabs.includes(initialTab) ? initialTab : "dashboard";
  return (
    <div
      className="h-full overflow-y-auto overflow-x-hidden overscroll-contain bg-white text-slate-900"
      style={{ scrollbarGutter: "stable", WebkitOverflowScrolling: "touch" }}
      data-testid="superadmin-console"
    >
      <div className="px-6 py-6 border-b border-slate-200">
        <Link
          to="/app"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate-500 hover:text-brand-blue transition-colors"
          data-testid="sa-back"
        >
          <ArrowLeft size={12} /> Workspace
        </Link>
        <div className="mt-3 flex items-center justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 h-6 px-3 rounded-full bg-brand-blue/15 border border-brand-blue/40 text-brand-blue text-[10px] uppercase tracking-[0.28em] font-semibold">
              <ShieldCheck size={12} weight="fill" /> Super Admin
            </div>
            <h1 className="mt-3 font-heading font-black text-4xl tracking-tight">Management Console</h1>
            <p className="mt-2 text-[13px] text-slate-500 max-w-2xl">
              Complete oversight across users, companies and projects. Every action here is audit-logged.
              Only accounts with the <span className="text-brand-blue font-semibold">superadmin</span> role can open this page.
            </p>
          </div>
          <Link to="/app/admin" className="text-[12px] text-brand-blue hover:underline inline-flex items-center gap-1">
            <Article size={12} /> Legacy admin panel
          </Link>
        </div>
      </div>

      <div className="px-6 pt-2 pb-1">
        <YourDomainBanner />
      </div>

      {/* P37k · Executive KPI strip — aligned with the AI Project Setup
          design language: white cards, brand-blue icon halos, tight
          typography, live data from /api/enterprise/dashboard. */}
      <SuperAdminKPIStrip />

      <Tabs defaultValue={startTab} className="px-6 pt-4 pb-16">
        {/* P37w · Sticky tabs — the mandate requires the tab bar
            remains visible while the table/content scrolls.  We stick
            the entire TabsList (not just individual triggers) to the
            top of the scroll ancestor (the outer div above) so the
            header/KPI strip scrolls away while navigation stays put. */}
        <TabsList className="sticky top-0 z-30 bg-white border border-slate-200 rounded-md flex-wrap h-auto shadow-sm" data-testid="sa-tabs">
          <TabsTrigger value="dashboard" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-dashboard">
            <Sparkle size={13} className="mr-2" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="pending" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-pending">
            <ClockCounterClockwise size={13} className="mr-2" /> Pending
            <PendingCountBadge />
          </TabsTrigger>
          <TabsTrigger value="users" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-users">
            <Users size={13} className="mr-2" /> Users
          </TabsTrigger>
          <TabsTrigger value="companies" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-companies">
            <Buildings size={13} className="mr-2" /> Companies
          </TabsTrigger>
          <TabsTrigger value="projects" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-projects">
            <Folder size={13} className="mr-2" /> Projects
          </TabsTrigger>
          <TabsTrigger value="roles" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-roles">
            <ShieldCheck size={13} className="mr-2" /> Roles
          </TabsTrigger>
          <TabsTrigger value="permissions" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-permissions">
            <Key size={13} className="mr-2" /> Permissions
          </TabsTrigger>
          <TabsTrigger value="invitations" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-invitations">
            <EnvelopeSimple size={13} className="mr-2" /> Invitations
          </TabsTrigger>
          <TabsTrigger value="signatures" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-signatures">
            <Sparkle size={13} className="mr-2" /> Signature Registry
          </TabsTrigger>
          <TabsTrigger value="audit" className="text-xs uppercase tracking-widest data-[state=active]:bg-brand-blue data-[state=active]:text-white" data-testid="sa-tab-audit">
            <ClockCounterClockwise size={13} className="mr-2" /> Audit Logs
          </TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard"  className="mt-5"><EnterpriseDashboardTab /></TabsContent>
        <TabsContent value="pending"    className="mt-5"><PendingApprovalsTab /></TabsContent>
        <TabsContent value="users"     className="mt-5"><UsersTab /></TabsContent>
        <TabsContent value="companies" className="mt-5"><CompaniesTab /></TabsContent>
        <TabsContent value="projects"  className="mt-5"><ProjectsTab /></TabsContent>
        <TabsContent value="roles"       className="mt-5"><RolesTab /></TabsContent>
        <TabsContent value="permissions" className="mt-5"><PermissionsTab /></TabsContent>
        <TabsContent value="invitations" className="mt-5"><InvitationsTab /></TabsContent>
        <TabsContent value="signatures"  className="mt-5"><SignatureRegistryTab /></TabsContent>
        <TabsContent value="audit"     className="mt-5"><AuditLogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}


/* ================================================================== *
 * SuperAdminKPIStrip — P37k                                           *
 * 5 executive KPI cards matching the mockup layout: Total Users,     *
 * Companies, Projects, Super Admins, Storage Used (with progress).   *
 * ================================================================== */
function SuperAdminKPIStrip() {
  const { data } = useQuery({
    queryKey: ["enterprise-dashboard-kpis"],
    queryFn: getEnterpriseDashboard,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const kpis = data?.kpis || {};
  const bytes = kpis.storage_bytes || 0;
  const storageGB = bytes / (1024 * 1024 * 1024);
  const storageQuotaGB = kpis.storage_quota_gb || 500;
  const storagePct = Math.min(100, Math.round((storageGB / Math.max(1, storageQuotaGB)) * 100));

  const cards = [
    { key: "users",     label: "Total Users",   value: kpis.users ?? "—",
      icon: Users,     tone: "brand-blue",  sub: `+${kpis.users_new_7d ?? 0} this week` },
    { key: "companies", label: "Companies",     value: kpis.companies ?? "—",
      icon: Buildings, tone: "amber-500",  sub: `${kpis.companies_active ?? kpis.companies ?? 0} active` },
    { key: "projects",  label: "Projects",      value: kpis.projects ?? "—",
      icon: Folder,    tone: "emerald-500", sub: `${kpis.cp_approved ?? 0} CP-approved` },
    { key: "admins",    label: "Super Admins",  value: kpis.superadmins ?? "1",
      icon: ShieldCheck, tone: "rose-500",  sub: "Highest privilege" },
  ];
  return (
    <div className="px-6 pt-4 pb-2" data-testid="sa-kpi-strip">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {cards.map((c) => (
          <div key={c.key}
               className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3"
               data-testid={`sa-kpi-${c.key}`}>
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 bg-${c.tone}/10 text-${c.tone}`}>
              <c.icon size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">
                {c.label}
              </div>
              <div className="text-2xl font-black text-slate-900 leading-tight truncate"
                   data-testid={`sa-kpi-${c.key}-value`}>
                {typeof c.value === "number" ? c.value.toLocaleString() : c.value}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">{c.sub}</div>
            </div>
          </div>
        ))}
        {/* Storage card with progress bar */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"
             data-testid="sa-kpi-storage">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-cyan-500/10 text-cyan-600 flex items-center justify-center shrink-0">
              <Article size={18} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Storage Used</div>
              <div className="text-2xl font-black text-slate-900 leading-tight truncate"
                   data-testid="sa-kpi-storage-value">
                {storageGB > 0 ? `${storageGB.toFixed(1)} GB` : "—"}
              </div>
              <div className="text-[10.5px] text-slate-400 mt-0.5">of {storageQuotaGB} GB quota</div>
            </div>
          </div>
          <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-cyan-500 rounded-full transition-all"
              style={{ width: `${storagePct}%` }}
              data-testid="sa-kpi-storage-bar"
            />
          </div>
        </div>
      </div>
    </div>
  );
}


/* ================================================================== *
 * AuditLogsTab — P37k                                                 *
 * Read-only chronological audit list from /api/enterprise/dashboard   *
 * (which returns the last 15 events). Full search will land in a     *
 * follow-up sprint.                                                   *
 * ================================================================== */
function AuditLogsTab() {
  const { data } = useQuery({
    queryKey: ["enterprise-dashboard-audit"],
    queryFn: getEnterpriseDashboard,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const events = data?.recent || [];
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden"
         data-testid="sa-audit-logs">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          Recent audit events · <span className="text-slate-900 font-semibold">{events.length}</span>
        </div>
        <div className="text-[10.5px] text-slate-400 font-mono">Auto-refreshes every 60 s</div>
      </div>
      {events.length === 0 && (
        <div className="p-12 text-center text-slate-400 text-[12px]" data-testid="sa-audit-empty">
          No audit events yet
        </div>
      )}
      <div className="divide-y divide-slate-100">
        {events.map((e, i) => (
          <div key={i} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50/70"
               data-testid={`sa-audit-row-${i}`}>
            <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
              <ClockCounterClockwise size={13} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-900">
                <span data-testid={`sa-audit-kind-${i}`}>{e.kind || "event"}</span>
                {e.project_id && (
                  <span className="text-[10px] font-mono text-slate-400">
                    · {String(e.project_id).slice(0, 8)}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {e.user_email || "system"}
              </div>
            </div>
            <div className="text-[10.5px] text-slate-400 font-mono">
              {e.ts ? new Date(e.ts).toLocaleString() : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ================================================================== *
 * Reusable helpers                                                    *
 * ================================================================== */
function useSelection(initial = []) {
  const [selected, setSelected] = useState(new Set(initial));
  const toggle = (id) => setSelected((s) => {
    const next = new Set(s); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const setAll = (ids) => setSelected(new Set(ids));
  const clear = () => setSelected(new Set());
  return { selected, toggle, setAll, clear, count: selected.size };
}

function SectionShell({ title, count, children, actions }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between gap-4 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">
          {title} · <span className="text-slate-900 font-semibold">{count}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">{actions}</div>
      </div>
      {children}
    </div>
  );
}

function TempPasswordDialog({ open, onClose, result }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(result?.temporary_password || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white border-slate-200" data-testid="sa-temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkle className="text-brand-blue" weight="fill" /> Temporary password issued</DialogTitle>
          <DialogDescription>
            Share this with the affected user{result?.affected_users?.length > 1 ? "s" : ""} through a secure channel.
            They will be forced to change it on their next sign-in.
          </DialogDescription>
        </DialogHeader>
        <div className="p-4 rounded-lg border border-brand-blue/40 bg-brand-blue/10 font-mono text-lg text-slate-900 select-all break-all">
          {result?.temporary_password}
        </div>
        {result?.email ? (
          <div className="text-[13px] text-slate-500">For: <b className="text-slate-900">{result.email}</b></div>
        ) : null}
        {result?.affected_users?.length ? (
          <div className="text-[13px] text-slate-500">
            Applied to <b className="text-slate-900">{result.affected_users.length}</b> user{result.affected_users.length > 1 ? "s" : ""}.
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={copy}>
            {copied ? <><CheckCircle size={14} className="mr-2 text-emerald-600" /> Copied</> : <><Copy size={14} className="mr-2" /> Copy</>}
          </Button>
          <Button className="bg-brand-blue hover:bg-brand-blueDeep text-white" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== *
 * USERS TAB                                                           *
 * ================================================================== */
function UsersTab() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["sa-users"], queryFn: saListUsers });
  const [search, setSearch] = useState("");
  const [tempResult, setTempResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const sel = useSelection();

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (data || []).filter((u) =>
      !q ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.company || "").toLowerCase().includes(q)
    );
  }, [data, search]);

  const del = useMutation({
    mutationFn: saDeleteUser,
    onSuccess: () => { toast.success("User deleted"); qc.invalidateQueries({ queryKey: ["sa-users"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  const bulkDel = useMutation({
    mutationFn: () => saBulkDeleteUsers(Array.from(sel.selected)),
    onSuccess: (r) => {
      toast.success(`${r.deleted_count} user${r.deleted_count === 1 ? "" : "s"} deleted${r.skipped_protected ? ` · ${r.skipped_protected} protected skipped` : ""}`);
      sel.clear();
      qc.invalidateQueries({ queryKey: ["sa-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk delete failed"),
  });
  const reset = useMutation({
    mutationFn: (id) => saResetUserPassword(id),
    onSuccess: (r) => setTempResult(r),
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  return (
    <SectionShell
      title={`${sel.count} of ${filtered.length} selected`}
      count={filtered.length}
      actions={
        <>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email · name · company"
              className="pl-9 h-9 w-64 bg-white border-slate-200"
              data-testid="sa-users-search"
            />
          </div>
          <Button
            variant="destructive"
            size="sm"
            disabled={!sel.count}
            onClick={() => { if (confirm(`Delete ${sel.count} user(s)? Super-admins are protected.`)) bulkDel.mutate(); }}
            data-testid="sa-users-bulk-delete"
          >
            <Trash size={13} className="mr-1.5" /> Delete selected
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="p-8 text-center text-slate-500 text-sm">Loading users…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <Checkbox
                    checked={sel.count > 0 && sel.count === filtered.length}
                    onCheckedChange={(v) => v ? sel.setAll(filtered.map((u) => u.id)) : sel.clear()}
                    data-testid="sa-users-select-all"
                  />
                </th>
                <th className="px-4 py-3 text-left">User</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-slate-200/50 hover:bg-white/[0.02]" data-testid={`sa-user-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <Checkbox checked={sel.selected.has(u.id)} onCheckedChange={() => sel.toggle(u.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{u.name || "—"}</div>
                    <div className="text-slate-500 text-[12px]">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{u.company || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full uppercase tracking-widest ${
                      u.role === "superadmin" ? "bg-brand-blue/15 text-brand-blue" :
                      u.role === "admin" ? "bg-amber-500/15 text-amber-400" : "bg-white/5 text-slate-500"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{u.status}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`sa-user-edit-${u.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-brand-blue"
                        onClick={() => { if (confirm(`Reset password for ${u.email}?`)) reset.mutate(u.id); }}
                        data-testid={`sa-user-reset-${u.id}`}>
                        <Key size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                        onClick={() => { if (confirm(`Delete ${u.email}?`)) del.mutate(u.id); }}
                        data-testid={`sa-user-delete-${u.id}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No users match this search.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      <TempPasswordDialog open={!!tempResult} onClose={() => setTempResult(null)} result={tempResult} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} />
    </SectionShell>
  );
}

function EditUserDialog({ user, onClose }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  useMemo(() => {
    if (user) setForm({ name: user.name || "", company: user.company || "", role: user.role || "user", status: user.status || "approved" });
  }, [user]);
  if (!user) return null;
  const save = async () => {
    try {
      await saUpdateUser(user.id, form);
      toast.success("User updated");
      qc.invalidateQueries({ queryKey: ["sa-users"] });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg bg-white border-slate-200" data-testid="sa-edit-user-dialog">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Name</Label><Input value={form.name || ""} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="sa-edit-name" /></div>
          <div><Label>Company</Label><Input value={form.company || ""} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} data-testid="sa-edit-company" /></div>
          <div><Label>Role</Label>
            <select value={form.role || ""} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              className="w-full h-10 rounded-md bg-white border border-slate-200 text-slate-900 px-3" data-testid="sa-edit-role">
              <option value="user">user</option><option value="admin">admin</option><option value="superadmin">superadmin</option>
            </select>
          </div>
          <div><Label>Status</Label>
            <select value={form.status || ""} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="w-full h-10 rounded-md bg-white border border-slate-200 text-slate-900 px-3" data-testid="sa-edit-status">
              <option value="pending">pending</option><option value="approved">approved</option><option value="rejected">rejected</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand-blue hover:bg-brand-blueDeep text-white" onClick={save} data-testid="sa-edit-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== *
 * COMPANIES TAB                                                       *
 * ================================================================== */
function CompaniesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sa-companies"], queryFn: saListCompanies });
  const rows = data?.companies || [];
  const sel = useSelection();
  const [tempResult, setTempResult] = useState(null);
  const [renaming, setRenaming] = useState(null);

  const del = useMutation({
    mutationFn: saDeleteCompany,
    onSuccess: (r) => { toast.success(`Company deleted · ${r.users_deleted} users · ${r.projects_deleted} projects`); qc.invalidateQueries({ queryKey: ["sa-companies"] }); qc.invalidateQueries({ queryKey: ["sa-users"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  const bulkDel = useMutation({
    mutationFn: () => saBulkDeleteCompanies(Array.from(sel.selected)),
    onSuccess: (r) => { toast.success(`${r.companies_deleted} companies deleted`); sel.clear(); qc.invalidateQueries({ queryKey: ["sa-companies"] }); qc.invalidateQueries({ queryKey: ["sa-users"] }); },
  });
  const reset = useMutation({
    mutationFn: (name) => saResetCompanyPassword(name),
    onSuccess: (r) => setTempResult(r),
    onError: (e) => toast.error(e?.response?.data?.detail || "Reset failed"),
  });

  return (
    <SectionShell
      title={`${sel.count} of ${rows.length} selected`}
      count={rows.length}
      actions={
        <Button variant="destructive" size="sm" disabled={!sel.count}
          onClick={() => { if (confirm(`Delete ${sel.count} company(ies) and every non-superadmin user + project inside them?`)) bulkDel.mutate(); }}
          data-testid="sa-companies-bulk-delete">
          <Trash size={13} className="mr-1.5" /> Delete selected
        </Button>
      }
    >
      {isLoading ? (
        <div className="p-8 text-center text-slate-500 text-sm">Loading companies…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <Checkbox
                    checked={sel.count > 0 && sel.count === rows.length}
                    onCheckedChange={(v) => v ? sel.setAll(rows.map((r) => r.name)) : sel.clear()}
                    data-testid="sa-companies-select-all"
                  />
                </th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-right">Users</th>
                <th className="px-4 py-3 text-right">Admins</th>
                <th className="px-4 py-3 text-right">Projects</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.name} className="border-t border-slate-200/50 hover:bg-white/[0.02]" data-testid={`sa-company-row-${c.name}`}>
                  <td className="px-4 py-3">
                    <Checkbox checked={sel.selected.has(c.name)} onCheckedChange={() => sel.toggle(c.name)} />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.user_count}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.admin_count}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.project_count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setRenaming(c)} data-testid={`sa-company-rename-${c.name}`}>
                        <PencilSimple size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-brand-blue"
                        onClick={() => { if (confirm(`Issue a shared temp password for every user in ${c.name}?`)) reset.mutate(c.name); }}
                        data-testid={`sa-company-reset-${c.name}`}>
                        <Key size={13} />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                        onClick={() => { if (confirm(`Delete company "${c.name}" and every non-superadmin user + project inside it?`)) del.mutate(c.name); }}
                        data-testid={`sa-company-delete-${c.name}`}>
                        <Trash size={13} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No companies registered yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
      <TempPasswordDialog open={!!tempResult} onClose={() => setTempResult(null)} result={tempResult} />
      <RenameCompanyDialog target={renaming} onClose={() => setRenaming(null)} />
    </SectionShell>
  );
}

function RenameCompanyDialog({ target, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  useMemo(() => { if (target) setName(target.name); }, [target]);
  if (!target) return null;
  const save = async () => {
    try {
      await saRenameCompany(target.name, name);
      toast.success("Company renamed");
      qc.invalidateQueries({ queryKey: ["sa-companies"] });
      qc.invalidateQueries({ queryKey: ["sa-users"] });
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white border-slate-200" data-testid="sa-rename-company-dialog">
        <DialogHeader>
          <DialogTitle>Rename company</DialogTitle>
          <DialogDescription>Renames the company across every associated user.</DialogDescription>
        </DialogHeader>
        <Label>New name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="sa-rename-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand-blue hover:bg-brand-blueDeep text-white" onClick={save} data-testid="sa-rename-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ================================================================== *
 * PROJECTS TAB                                                        *
 * ================================================================== */
function ProjectsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sa-projects"], queryFn: saListProjects });
  const rows = useMemo(() => data?.projects || [], [data]);
  const [search, setSearch] = useState("");
  const sel = useSelection();

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((p) =>
      !q ||
      (p.name || "").toLowerCase().includes(q) ||
      (p.commodity || "").toLowerCase().includes(q) ||
      (p.company || "").toLowerCase().includes(q) ||
      (p.owner_email || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const del = useMutation({
    mutationFn: saDeleteProject,
    onSuccess: () => { toast.success("Project deleted"); qc.invalidateQueries({ queryKey: ["sa-projects"] }); },
  });
  const bulkDel = useMutation({
    mutationFn: () => saBulkDeleteProjects(Array.from(sel.selected)),
    onSuccess: (r) => { toast.success(`${r.deleted_count} project(s) deleted`); sel.clear(); qc.invalidateQueries({ queryKey: ["sa-projects"] }); },
  });

  return (
    <SectionShell
      title={`${sel.count} of ${filtered.length} selected`}
      count={filtered.length}
      actions={
        <>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name · commodity · owner"
              className="pl-9 h-9 w-64 bg-white border-slate-200" data-testid="sa-projects-search" />
          </div>
          <Button variant="destructive" size="sm" disabled={!sel.count}
            onClick={() => { if (confirm(`Delete ${sel.count} project(s) and every block model, pit and export tied to them?`)) bulkDel.mutate(); }}
            data-testid="sa-projects-bulk-delete">
            <Trash size={13} className="mr-1.5" /> Delete selected
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="p-8 text-center text-slate-500 text-sm">Loading projects…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left w-10">
                  <Checkbox
                    checked={sel.count > 0 && sel.count === filtered.length}
                    onCheckedChange={(v) => v ? sel.setAll(filtered.map((p) => p.id)) : sel.clear()}
                    data-testid="sa-projects-select-all"
                  />
                </th>
                <th className="px-4 py-3 text-left">Project</th>
                <th className="px-4 py-3 text-left">Commodity</th>
                <th className="px-4 py-3 text-left">Owner</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-slate-200/50 hover:bg-white/[0.02]" data-testid={`sa-project-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <Checkbox checked={sel.selected.has(p.id)} onCheckedChange={() => sel.toggle(p.id)} />
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/app/projects/${p.id}`} className="font-medium text-slate-900 hover:text-brand-blue transition-colors">{p.name}</Link>
                    <div className="text-slate-500 text-[11px] font-mono">{p.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[11px] px-2 py-0.5 rounded-full uppercase tracking-widest bg-white/5 text-slate-500">
                      {p.commodity || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-slate-900">{p.owner_name || "—"}</div>
                    <div className="text-slate-500 text-[11px]">{p.owner_email || "—"}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-900">{p.company || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                      onClick={() => { if (confirm(`Delete project "${p.name}"?`)) del.mutate(p.id); }}
                      data-testid={`sa-project-delete-${p.id}`}>
                      <Trash size={13} />
                    </Button>
                  </td>
                </tr>
              ))}
              {!filtered.length ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No projects match this search.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </SectionShell>
  );
}


/* ================================================================== *
 * Pending Approvals — universal SRES gate                             *
 * ================================================================== */

function PendingCountBadge() {
  const { data } = useQuery({
    queryKey: ["pending-users"],
    queryFn: adminPending,
    refetchInterval: 15_000,
  });
  const n = (data?.pending || []).length;
  if (n === 0) return null;
  return (
    <span
      className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold"
      data-testid="sa-pending-badge"
    >
      {n}
    </span>
  );
}

function PendingApprovalsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["pending-users"],
    queryFn: adminPending,
  });
  const users = data?.pending || [];

  const approve = useMutation({
    mutationFn: ({ uid, body }) => adminApprove(uid, body),
    onSuccess: (res, vars) => {
      const emailed = res?.email_notification?.sent;
      toast.success(
        emailed
          ? "Approved · notification email sent"
          : "Approved · email delivery skipped (see audit log)"
      );
      qc.invalidateQueries({ queryKey: ["pending-users"] });
      qc.invalidateQueries({ queryKey: ["sa-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Approval failed"),
  });
  const reject = useMutation({
    mutationFn: (uid) => adminReject(uid),
    onSuccess: () => {
      toast.success("Rejected");
      qc.invalidateQueries({ queryKey: ["pending-users"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Reject failed"),
  });

  return (
    <SectionShell title="Pending approvals" count={users.length} actions={null}>
      <div className="px-5 py-3 border-b border-slate-100 text-[12px] text-slate-500 leading-relaxed bg-slate-50/40">
        Every SRES account — including staff invited by company owners — lands here until you approve it.
        Approvals fire an automated welcome email to the user; rejections do not.
      </div>
      {isLoading ? (
        <div className="p-8 text-[12px] text-slate-500 mono">Loading pending users…</div>
      ) : users.length === 0 ? (
        <div className="p-10 text-center text-[13px] text-slate-500 flex flex-col items-center gap-3" data-testid="pending-empty">
          <CheckCircle size={28} className="text-emerald-500" weight="regular" />
          Nothing to review — every account is currently active.
        </div>
      ) : (
        <div className="overflow-x-auto table-wrapper">
          <table className="w-full text-[13px]" data-testid="pending-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">User</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Signed up</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <PendingRow key={u.id} user={u} onApprove={approve.mutate} onReject={reject.mutate} pending={approve.isPending || reject.isPending} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionShell>
  );
}

function PendingRow({ user, onApprove, onReject, pending }) {
  const today = new Date().toISOString().slice(0, 10);
  const twoYears = new Date(); twoYears.setFullYear(twoYears.getFullYear() + 2);
  const twoYearsIso = twoYears.toISOString().slice(0, 10);
  const [role, setRole] = useState(user.role || "user");
  const [company, setCompany] = useState(user.company || user.organisation || "");
  const [validFrom, setValidFrom] = useState(today);
  const [validTo, setValidTo] = useState(twoYearsIso);

  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`pending-row-${user.id}`}>
      <td className="px-4 py-4">
        <div className="font-semibold text-slate-900 leading-tight">{user.name || "—"}</div>
        <div className="text-[12px] text-slate-500 mono">{user.email}</div>
      </td>
      <td className="px-4 py-4">
        <Input
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          placeholder="Company"
          className="h-9 bg-white border-slate-200 rounded-sm text-[13px]"
          data-testid={`pending-company-${user.id}`}
        />
      </td>
      <td className="px-4 py-4 text-[12px] mono text-slate-500 whitespace-nowrap">
        {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
      </td>
      <td className="px-4 py-4">
        <div className="flex flex-wrap items-end gap-2 justify-end">
          <div className="grow-0">
            <Label className="text-[10px] uppercase text-slate-500">Role</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mt-1 h-9 rounded-sm border border-slate-200 bg-white text-[13px] px-2 min-w-[110px]"
              data-testid={`pending-role-${user.id}`}
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <div className="grow-0">
            <Label className="text-[10px] uppercase text-slate-500">Valid from</Label>
            <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
              className="mt-1 h-9 bg-white border-slate-200 rounded-sm text-[12px] w-[130px]"
              data-testid={`pending-vfrom-${user.id}`} />
          </div>
          <div className="grow-0">
            <Label className="text-[10px] uppercase text-slate-500">Valid to</Label>
            <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)}
              className="mt-1 h-9 bg-white border-slate-200 rounded-sm text-[12px] w-[130px]"
              data-testid={`pending-vto-${user.id}`} />
          </div>
          <Button
            onClick={() => onApprove({ uid: user.id, body: { role, company: company.trim(), valid_from: validFrom, valid_to: validTo } })}
            disabled={pending}
            className="h-9 bg-brand-blue hover:bg-brand-blueDeep text-white text-[12px] font-semibold rounded-md inline-flex items-center gap-1.5"
            data-testid={`pending-approve-${user.id}`}
          >
            <Check size={13} weight="bold" /> Approve
          </Button>
          <Button
            onClick={() => window.confirm(`Reject ${user.email}?`) && onReject(user.id)}
            disabled={pending}
            variant="outline"
            className="h-9 border-slate-300 text-slate-700 hover:border-red-400 hover:text-red-600 text-[12px] font-semibold rounded-md inline-flex items-center gap-1.5"
            data-testid={`pending-reject-${user.id}`}
          >
            <XCircle size={13} weight="regular" /> Reject
          </Button>
        </div>
      </td>
    </tr>
  );
}




// ═══════════════════════════════════════════════════════════════════════
// Enterprise Dashboard tab (P18a · Feb 2026)
// ═══════════════════════════════════════════════════════════════════════
function EnterpriseDashboardTab() {
  const q = useQuery({
    queryKey: ["enterprise-dashboard"],
    queryFn:  () => getEnterpriseDashboard(),
    refetchInterval: 30_000,
    retry: false,
  });
  if (q.isLoading) return <div className="text-[13px] text-slate-500 py-8 text-center">Loading enterprise KPIs…</div>;
  if (q.error)      return <div className="text-[13px] text-rose-600 py-8 text-center">Failed to load dashboard: {q.error?.response?.data?.detail || q.error?.message}</div>;
  const d = q.data || {};
  const k = d.kpis || {};
  const kpiTiles = [
    { key: "projects",           label: "Projects",             value: k.projects,           tone: "blue" },
    { key: "projects_with_data", label: "With data uploaded",  value: k.projects_with_data, tone: "sky" },
    { key: "cp_approved",        label: "CP-approved",          value: k.cp_approved,        tone: "emerald" },
    { key: "block_models",       label: "Block models",         value: k.block_models,       tone: "amber" },
    { key: "approved_boundaries",label: "Approved boundaries", value: k.approved_boundaries, tone: "sky" },
    { key: "running_jobs",       label: "Running jobs",         value: k.running_jobs,       tone: "amber" },
    { key: "users",              label: "Users",                value: k.users,              tone: "slate" },
    { key: "audit_events",       label: "Audit events",         value: k.audit_events,       tone: "slate" },
  ];
  const commodityList = Object.entries(d.by_commodity || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-6" data-testid="sa-enterprise-dashboard">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpiTiles.map((t) => (
          <div key={t.key}
                className={`rounded border p-3 bg-white flex flex-col gap-1 border-slate-200`}
                data-testid={`sa-kpi-${t.key}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              {t.label}
            </div>
            <div className="text-2xl font-black text-slate-900">
              {(t.value ?? 0).toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Diversity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
            Countries ({(d.diversity?.countries || []).length})
          </div>
          <div className="flex flex-wrap gap-1">
            {(d.diversity?.countries || []).map((c) => (
              <span key={c} className="inline-block text-[11px] bg-slate-100 border border-slate-200 rounded px-2 py-0.5 font-mono text-slate-700">
                {c}
              </span>
            ))}
            {!d.diversity?.countries?.length && <span className="text-[12px] text-slate-500">No countries set on any project yet.</span>}
          </div>
        </div>
        <div className="rounded border border-slate-200 bg-white p-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
            By commodity
          </div>
          <div className="space-y-1">
            {commodityList.map(([c, n]) => (
              <div key={c} className="flex items-center gap-2 text-[12px] font-mono">
                <span className="w-10 text-slate-700">{c}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-brand-blue" style={{ width: `${(n / (commodityList[0][1] || 1)) * 100}%` }} />
                </div>
                <span className="w-6 text-right text-slate-800 font-bold">{n}</span>
              </div>
            ))}
            {!commodityList.length && <span className="text-[12px] text-slate-500">No commodity distribution to show yet.</span>}
          </div>
        </div>
      </div>

      {/* Pending approvals */}
      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
          Pending CP approvals ({(d.pending_approvals || []).length})
        </div>
        {d.pending_approvals?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-slate-500 border-b border-slate-200">
                  <th className="text-left py-1 px-2">Project</th>
                  <th className="text-left py-1 px-2">Commodity</th>
                  <th className="text-right py-1 px-2">Open</th>
                </tr>
              </thead>
              <tbody>
                {d.pending_approvals.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100"
                       data-testid={`sa-pending-approval-${p.id}`}>
                    <td className="py-1 px-2">{p.name}</td>
                    <td className="py-1 px-2 font-mono">{p.commodity}</td>
                    <td className="py-1 px-2 text-right">
                      <Link className="text-brand-blue hover:underline text-[11px]"
                             to={`/app/projects/${p.id}/wizard/cpr`}
                             data-testid={`sa-pending-open-${p.id}`}>
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-[12px] text-slate-500">No projects awaiting CP approval.</div>
        )}
      </div>

      {/* Recent activity */}
      <div className="rounded border border-slate-200 bg-white p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
          Recent audit events
        </div>
        {d.recent_activity?.length ? (
          <div className="space-y-1 font-mono text-[11px]">
            {d.recent_activity.map((e, i) => (
              <div key={i} className="flex items-center gap-2 border-b border-slate-100 py-1">
                <span className="text-slate-500 w-40 shrink-0 truncate">{e.ts}</span>
                <span className="w-28 shrink-0 truncate text-slate-700">{e.user_email}</span>
                <span className="flex-1 truncate text-slate-800">{e.kind}</span>
                {e.project_id && <span className="text-slate-500 truncate">proj:{e.project_id.slice(0, 6)}</span>}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-[12px] text-slate-500">No audit events yet.</div>
        )}
      </div>

      <div className="text-[10px] text-slate-500 font-mono">
        Scope: {d.user_scope} · generated {new Date(d.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
